import React, { useState, useEffect, useRef } from "react";
import { useAccount, useSignMessage } from "wagmi";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { ethers, BigNumber } from "ethers";
import { abi } from "../components/ABI";
import { getNFTCollection } from "../api";
import "./nft.scss";

const NFTAuth = (props) => {
  const { address, isConnected } = useAccount();
  const [nftCount, setNftCount] = useState(0);
  const [tokenIds, setTokenIds] = useState([]);
  const [isError, setIsError] = useState(false);
  const [signature, setSignature] = useState(null);
  const [contractAddresses, setContractAddresses] = useState([]);
  const [balances, setBalances] = useState([]);

  // Fetch contract addresses from API
  useEffect(() => {
    async function fetchContracts() {
      try {
        const res = await getNFTCollection();
        // Adjust this if the API response structure is different
        const contracts = res?.data?.map((item) => item.contractAddress) || [];
        setContractAddresses(contracts);
      } catch (e) {
        setContractAddresses([]);
      }
    }
    fetchContracts();
  }, []);

  // Read balances for all contracts
  useEffect(() => {
    if (!isConnected || !address || contractAddresses.length === 0) {
      setBalances([]);
      setNftCount(0);
      return;
    }
    let cancelled = false;
    async function fetchBalances() {
      try {
        // Use ethers.js to read balances
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const results = await Promise.all(
          contractAddresses.map(async (contract) => {
            try {
              const nftContract = new ethers.Contract(contract, abi, provider);
              const balance = await nftContract.balanceOf(address);
              return BigNumber.from(balance);
            } catch (e) {
              return BigNumber.from(0);
            }
          })
        );
        if (!cancelled) {
          setBalances(results);
          const total = results.reduce(
            (acc, b) => acc.add(b),
            BigNumber.from(0)
          );
          setNftCount(total.toNumber());
        }
      } catch (e) {
        setBalances([]);
        setNftCount(0);
      }
    }
    fetchBalances();
    return () => {
      cancelled = true;
    };
  }, [contractAddresses, address, isConnected]);

  const {
    data: signMessageData,
    error: testError,
    isLoading: check,
    signMessage,
    variables,
  } = useSignMessage();

  // Prevent infinite loop by only setting ownerId if changed
  const lastOwnerId = useRef();
  useEffect(() => {
    if (signMessageData) {
      if (address && props.setownerId) {
        if (lastOwnerId.current !== address) {
          props.setownerId(address);
          lastOwnerId.current = address;
        }
      }
      setSignature(signMessageData);
    }
  }, [signMessageData, address]);

  useEffect(() => {
    if (!isConnected) {
      setNftCount(0);
      setTokenIds([]);
      setSignature(null);
      setIsError(false);
      if (props.setownerId && lastOwnerId.current !== "") {
        props.setownerId("");
        lastOwnerId.current = "";
      }
    }
  }, [isConnected]);

  const handleSignMessage = async (event) => {
    event?.preventDefault();
    const ownedContracts = contractAddresses.filter(
      (_, idx) => balances[idx] && balances[idx].gt(0)
    );
    const message =
      ownedContracts.length === 1
        ? `I am verifying my ownership of NFTs at ${ownedContracts[0]} with address ${address}`
        : `I am verifying my ownership of NFTs at ${ownedContracts.join(
            " and "
          )} with address ${address}`;
    try {
      if (typeof signMessage === "function") {
        await signMessage({ message });
      } else {
        console.error("signMessage is not a function");
      }
    } catch (error) {
      console.error("Sign Message Error:", error);
    }
  };

  return (
    <div>
      <ConnectButton />
      {isConnected && (
        <>
          <p className="labelStyle">Wallet Address: {address}</p>
          {nftCount > 0 ? (
            <div>
              <p style={{ marginTop: "0px" }} className="labelStyle">
                You own {nftCount} Station3 Passes
              </p>
              {signature ? (
                <p style={{ marginTop: "0px" }} className="labelStyle">
                  Ownership Verified Successfully
                </p>
              ) : (
                <button
                  className="verify-ownership-button"
                  onClick={handleSignMessage}
                >
                  Verify Ownership
                </button>
              )}
            </div>
          ) : (
            <p className="errorText">You do not own any Station3 Passes.</p>
          )}
        </>
      )}
    </div>
  );
};

export default NFTAuth;
